<template>
  <b-container class="pt-5" fluid>
    <b-row>
      <b-col
        v-show="errorSiteMessage != null"
        class="pb-2"
        cols="10"
        offset="1"
      >
        <b-alert :show="errorSiteMessage != null" variant="danger">
          {{ errorSiteMessage }}
        </b-alert>
      </b-col>
      <b-col cols="10" offset="1">
        <b-alert show variant="primary">{{
          $t("subscriptions.disclaimer")
        }}</b-alert>
      </b-col>

      <b-col>
        <div class="pt-5 px-5">
          <div class="d-flex w-100 justify-content-between pb-2">
            <h3>
              {{ $t("subscriptions.previousSubscriptions") }}
            </h3>
            <b-btn
              v-show="errorSiteMessage == null"
              v-b-modal.subscribe-modal
              pill
              v-b-tooltip.hover
              :title="$t('subscriptions.addSubscription')"
              variant="light"
            >
              <i class="fas fa-plus fa-2x"></i>
            </b-btn>
          </div>

          <b-list-group>
            <b-list-group-item
              v-for="sub in getSubTranslate()"
              variant="warning"
              :key="sub.id"
              href="#"
            >
              <div class="d-flex w-100 justify-content-between">
                <h5 class="mb-1" style="font-weight: bold">
                  {{ sub.granularityT }}
                </h5>
                <small>{{ sub.duration }}</small>
              </div>

              <div class="pl-1" style="text-align: start; width: 50%">
                <span v-for="site in sub.siteNames" class="px-1" :key="site.id">
                  <b-badge :variant="site.variant" pill>{{
                    site.label
                  }}</b-badge>
                </span>
              </div>

              <div class="pt-2" style="text-align: start">
                <b-btn
                  variant="outline-danger"
                  size="sm"
                  pill
                  @click="() => unsubscribe(sub.id)"
                  >{{ $t("subscriptions.unsubscribe") }}</b-btn
                >
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-col>
    </b-row>

    <b-modal
      id="subscribe-modal"
      size="lg"
      ref="modal"
      center
      hide-footer
      scrollable
      :title="$t('subscriptions.addSubscription')"
      @hidden="reset"
    >
      <ValidationObserver ref="observer">
        <b-form @submit.prevent="checkFormValidity">
          <b-alert :show="errorMessage !== null" variant="danger">
            {{ errorMessage }}
          </b-alert>
          <b-text-input-with-validation
            v-model="form.employeeMail"
            rules="required|email"
            :label="$t('mail')"
            type="email"
          />
          <b-form-group
            :label="$t('subscriptions.granularity')"
            v-slot="{ ariaDescribedby }"
          >
            <b-form-radio
              v-for="radio in radios"
              v-model="form.granularity"
              :aria-describedby="ariaDescribedby"
              name="some-radios"
              :value="radio.value"
              :key="radio.value"
              >{{ radio.text }}</b-form-radio
            >
          </b-form-group>

          <b-tag-select
            v-model="form.sites"
            :options="optionSites"
            :title="$t('subscriptions.sites')"
            :search-no-result-message="$t('noResult')"
            :placeholder="$t('subscriptions.placeholder')"
            property-text="text"
            property-value="value"
          />

          <b-btn
            type="submit"
            :disabled="!form.employeeMail || !form.sites || !form.sites.length"
            variant="success"
            >{{ $t("subscriptions.subscribe") }}</b-btn
          >
        </b-form>
      </ValidationObserver>
    </b-modal>
  </b-container>
</template>

<script>
import moment from "moment";
import { get } from "lodash";
import { mapState } from "vuex";
import dimensionEnum from "@/model/dimensionEnum";
import { ValidationObserver } from "vee-validate";
import BTagSelect from "@/components/inputs/BTagSelect";
import BTextInputWithValidation from "@/components/inputs/BTextInputWithValidation";

import TableTools from "@/utils/tableTools";

export default {
  components: {
    BTextInputWithValidation,
    ValidationObserver,
    BTagSelect,
  },
  data() {
    return {
      form: {
        employeeMail: "",
        granularity: 0,
        sites: [],
      },

      errorMessage: null,
      optionSites: [],
      radios: [
        { value: 0, text: this.$t("subscriptions.granularities.weekend") },
        { value: 1, text: this.$t("subscriptions.granularities.weekly") },
        { value: 2, text: this.$t("subscriptions.granularities.monthly") },
        {
          value: 3,
          text: this.$t("subscriptions.granularities.maintenance_daily"),
        },
      ],
    };
  },
  computed: {
    ...mapState({
      subscriptions: (state) => state.mailSubscribe.subscriptions,
    }),
    errorSiteMessage() {
      return !this.optionSites.length
        ? this.$t("subscriptions.noSiteAvailable")
        : null;
    },
  },
  methods: {
    getSubTranslate() {
      // prepare the subscription to be displayed
      return (
        this.subscriptions
          .map((item) => {
            const granularities = [
              "weekend",
              "weekly",
              "monthly",
              "maintenance_daily",
            ];
            const granularity = granularities[item.granularity];
            const granularityT = this.$t(
              `subscriptions.granularities.${granularity}`
            );

            const today = new moment();
            const creationDate = moment(item.creationDate).utc("fr");

            const diff = creationDate.diff(today);
            const duration = moment
              .duration(diff)
              .humanize(true, { d: 7, w: 4 });

            const siteNames = item.sites.map((site) => {
              const el = this.optionSites.find(
                (e) => e.value.toLowerCase() === site.toLowerCase()
              );

              const label = el ? el.text : site;
              const variant = el ? "info" : "danger";

              return { label, variant, id: site };
            });

            return { ...item, granularityT, siteNames, duration };
          })
          // sort by date
          .sort((a, b) => {
            const d1 = moment(a.creationDate);
            const d2 = moment(b.creationDate);

            const isBefore = d1.isSameOrBefore(d2);

            return isBefore ? 1 : -1;
          })
      );
    },
    checkFormValidity() {
      this.$refs.observer.validate().then(async (valid) => {
        if (valid) {
          await this.subscribe();
        }
      });
    },
    reset() {
      this.form = {
        employeeMail: this.getMail,
        granularity: 0,
        sites: [],
      };
      this.errorMessage = null;
    },
    async subscribe() {
      // create a subscription in DB
      if (!this.form.sites.length) {
        this.errorMessage = this.$t("subscriptions.sitesEmpty");
        return;
      }

      this.errorMessage = null;

      try {
        await this.$store.dispatch("mailSubscribe/subscribe", this.form);

        // Hide the modal manually
        this.$nextTick(() => {
          this.$refs.modal.hide();
        });
      } catch (e) {
        const errorType = get(e, "response.data.error", null);

        const errorKey = errorType
          ? `subscriptions.errors.${errorType}`
          : "subscriptions.subscribeError";

        this.errorMessage = this.$t(errorKey);
      }
    },

    async unsubscribe(id) {
      try {
        const response = await this.$bvModal.msgBoxConfirm(
          this.$t("subscriptions.confirmSuppression"),
          {
            title: "Confirmation",
            size: "sm",
            buttonSize: "sm",
            okVariant: "info",
            okTitle: "Oui",
            cancelTitle: "Non",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true,
          }
        );

        if (!response) {
          return;
        }

        await this.$store.dispatch("mailSubscribe/unsubscribe", id);
      } catch (e) {
        console.error(e);
      }
    },
  },
  async mounted() {
    this.form.employeeMail = this.getMail;
    this.$store.dispatch("mailSubscribe/getSubscriptions", this.getMail);

    try {
      const dataQuery = {
        dimensions: [dimensionEnum.SITE_NAME, dimensionEnum.SITE],
      };

      const sites = await TableTools.Helper.doRequest(dataQuery);

      this.optionSites = sites.map((item) => {
        return { text: item["Site.label"], value: item["Site.id"] };
      });
    } catch (error) {
      this.optionSites = [];
      console.error(error);
    }
  },
};
</script>
